import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Lock, Eye, Database, Trash2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <FileText className="w-4 h-4" />
            Legal
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
              <p className="text-slate-500">Last updated: {currentDate}</p>
            </div>
          </div>

          {/* Quick Summary */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-50 rounded-xl p-4">
              <Lock className="w-5 h-5 text-blue-600 mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm">Your Data is Secure</h3>
              <p className="text-xs text-slate-500 mt-1">We use encryption and secure infrastructure</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <Eye className="w-5 h-5 text-purple-600 mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm">No Data Selling</h3>
              <p className="text-xs text-slate-500 mt-1">We never sell your personal information</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <Download className="w-5 h-5 text-green-600 mb-2" />
              <h3 className="font-semibold text-slate-900 text-sm">Your Rights</h3>
              <p className="text-xs text-slate-500 mt-1">Export or delete your data anytime</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <h2>1. Introduction</h2>
            <p>
              Decoded ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we 
              collect, use, disclose, and safeguard your information when you use our tariff classification platform.
            </p>

            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong>Profile Information:</strong> Company name, role, preferences</li>
              <li><strong>Product Data:</strong> Product descriptions, specifications, and images you upload for classification</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe; we do not store full card numbers</li>
              <li><strong>Communications:</strong> Messages you send us, feedback, and support requests</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Log Data:</strong> IP addresses, access times, referring URLs</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and improve our tariff classification services</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative messages, updates, and security alerts</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Train and improve our AI classification models (using anonymized data)</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
            </ul>

            <h2>4. AI and Machine Learning</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 my-4">
              <p className="m-0 text-blue-800 text-sm">
                <strong>Important:</strong> Product descriptions you submit may be used to improve our AI classification 
                accuracy. This data is anonymized and aggregated. You can opt out of AI training in your account settings.
              </p>
            </div>

            <h2>5. Information Sharing</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third parties who help us operate our platform (hosting, analytics, payment processing)</li>
              <li><strong>Team Members:</strong> If you're part of a team, shared analyses are visible to team members</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>

            <h2>6. Data Security</h2>
            <p>We implement appropriate security measures including:</p>
            <ul>
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure infrastructure hosted on reputable cloud providers</li>
            </ul>

            <h2>7. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide services. 
              You can request deletion of your data at any time. After account deletion, we may retain anonymized, 
              aggregated data for analytics purposes.
            </p>

            <h2>8. Your Privacy Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            
            <div className="grid md:grid-cols-2 gap-4 my-6">
              <div className="border rounded-xl p-4">
                <Database className="w-5 h-5 text-blue-600 mb-2" />
                <h4 className="font-semibold m-0 mb-1">Right to Access</h4>
                <p className="text-sm text-slate-600 m-0">Request a copy of all your personal data</p>
              </div>
              <div className="border rounded-xl p-4">
                <Trash2 className="w-5 h-5 text-red-600 mb-2" />
                <h4 className="font-semibold m-0 mb-1">Right to Deletion</h4>
                <p className="text-sm text-slate-600 m-0">Request permanent deletion of your account and data</p>
              </div>
              <div className="border rounded-xl p-4">
                <Download className="w-5 h-5 text-green-600 mb-2" />
                <h4 className="font-semibold m-0 mb-1">Right to Portability</h4>
                <p className="text-sm text-slate-600 m-0">Export your data in a machine-readable format</p>
              </div>
              <div className="border rounded-xl p-4">
                <Eye className="w-5 h-5 text-purple-600 mb-2" />
                <h4 className="font-semibold m-0 mb-1">Right to Opt-Out</h4>
                <p className="text-sm text-slate-600 m-0">Opt out of marketing communications and AI training</p>
              </div>
            </div>

            <p>
              To exercise these rights, visit Settings → Privacy in your account dashboard or contact us at privacy@decoded.com.
            </p>

            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate 
              safeguards are in place to protect your data in accordance with this Privacy Policy.
            </p>

            <h2>10. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 16. We do not knowingly collect personal information from 
              children under 16. If you believe we have collected such information, please contact us immediately.
            </p>

            <h2>11. Cookies and Tracking</h2>
            <p>We use cookies for:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use our platform</li>
            </ul>
            <p>
              You can manage cookie preferences in your browser settings. Note that disabling essential cookies may 
              affect platform functionality.
            </p>

            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes via email 
              or a prominent notice on our platform. Your continued use after changes constitutes acceptance.
            </p>

            <h2>13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or your data, contact us at:</p>
            <ul>
              <li>Email: privacy@decoded.com</li>
              <li>Data Protection Officer: dpo@decoded.com</li>
            </ul>

            <h2>14. GDPR Compliance (EU Users)</h2>
            <p>
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data 
              Protection Regulation (GDPR), including the right to lodge a complaint with your local data protection authority.
            </p>

            <h2>15. CCPA Compliance (California Users)</h2>
            <p>
              California residents have specific rights under the California Consumer Privacy Act (CCPA). We do not sell 
              personal information. For CCPA-related requests, contact us at privacy@decoded.com.
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="hover:text-blue-600">Terms of Service</Link>
          <span className="mx-3">•</span>
          <Link to="/" className="hover:text-blue-600">Return to Decoded</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
